import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ShoppingBag } from "lucide-react";
import { useCart } from "../contexts/CartContext";
import { useOpeningHours, hoursLine } from "./useBusinessInfo";

/**
 * Fixed marketing header (redesign shell).
 * - Utility strip: phone (tel:), hours, address (hours/address hidden <1080px).
 * - Main bar: emblem + wordmark, desktop nav with the Schools & Groups
 *   dropdown, then Book a Tour + the opal-shimmer "Buy Opal" button.
 * - Dropdowns: open on hover with a mouse, toggle on click/tap on touch.
 * - ≤1240px: desktop nav swaps for Book + Buy Opal + bag + hamburger; hamburger toggles
 *   a full-width dark panel listing every page.
 * - Bag button (desktop after Book a Tour, mobile between Book and ☰) opens
 *   the cart drawer that MarketingShell mounts.
 * All styling lives in marketing.css under .umr — no page CSS needed here.
 */
export default function SiteHeader() {
  const [menu, setMenu] = useState(null); // 'groups' | null
  const [mobileOpen, setMobileOpen] = useState(false);
  const lastPointerType = useRef("mouse");
  const hours = useOpeningHours();
  const { totalItems, setCartOpen } = useCart();
  const { pathname } = useLocation();
  const onShop = pathname.startsWith("/online-store") || pathname.startsWith("/opal-store");

  const buyOpal = (small) => (
    <Link
      to="/online-store"
      className={"u-btn-opal" + (small ? " u-btn-opal--sm" : "") + (onShop ? " is-current" : "")}
      onClick={closeAll}
    >
      <span>Buy Opal</span>
    </Link>
  );

  const bagButton = (extraClass) => (
    <button
      type="button"
      className={"u-bagbtn" + (extraClass ? " " + extraClass : "")}
      aria-label={totalItems ? `Shopping bag, ${totalItems} item${totalItems === 1 ? "" : "s"}` : "Shopping bag"}
      onClick={() => {
        closeAll();
        setCartOpen(true);
      }}
    >
      <ShoppingBag size={17} />
      {totalItems > 0 && <span className="u-bagcount">{totalItems}</span>}
    </button>
  );

  const closeAll = () => {
    setMenu(null);
    setMobileOpen(false);
  };

  // Close an open dropdown when clicking/tapping anywhere outside it.
  useEffect(() => {
    if (!menu) return undefined;
    const onDocClick = (e) => {
      if (!e.target.closest || !e.target.closest(".u-navdrop")) setMenu(null);
    };
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, [menu]);

  const hoverProps = (name) => ({
    onPointerEnter: (e) => {
      if (e.pointerType === "mouse") setMenu(name);
    },
    onPointerLeave: (e) => {
      if (e.pointerType === "mouse") setMenu(null);
    },
  });

  const labelProps = (name) => ({
    onPointerDown: (e) => {
      lastPointerType.current = e.pointerType || "mouse";
    },
    onClick: () => {
      setMenu((m) => {
        // Mouse users already opened via hover — a click keeps it open.
        if (m === name) return lastPointerType.current === "mouse" ? name : null;
        return name;
      });
    },
  });

  return (
    <header className="u-header">
      {/* top utility strip */}
      <div className="u-header__strip">
        <div className="u-header__strip-left">
          <a href="tel:+61886725288">📞 (08) 8672 5288</a>
          <span className="u-header__hours u-hidesm">{hoursLine(hours.summary)}</span>
        </div>
        <div className="u-header__addr u-hidesm">📍 14 Hutchison St, Coober Pedy SA 5723</div>
      </div>

      {/* main bar */}
      <div className="u-header__bar">
        <Link to="/" className="u-brand" onClick={closeAll}>
          <img src="/design/assets/umoona-emblem.png" alt="Umoona Opal Mine & Museum" className="u-brand__mark" />
          <span className="u-brand__stack">
            <span className="u-brand__name">UMOONA OPAL</span>
            <span className="u-brand__sub">MINE &amp; MUSEUM</span>
          </span>
        </Link>

        {/* desktop nav (>1240px) */}
        <nav className="u-desknav">
          <Link to="/about-opal">About Opal</Link>
          <Link to="/coober-pedy">Coober Pedy</Link>
          <Link to="/free-museum">Free Museum</Link>
          <Link to="/underground-tours">Tours</Link>

          <div className="u-navdrop" {...hoverProps("groups")}>
            <button type="button" className="u-navdrop__label" aria-expanded={menu === "groups"} {...labelProps("groups")}>
              Schools &amp; Groups <span className="u-navdrop__caret">▾</span>
            </button>
            {menu === "groups" && (
              <div className="u-navdrop__wrap">
                <div className="u-navdrop__panel">
                  <Link to="/group-tours" className="u-navdrop__link" onClick={closeAll}>
                    Group &amp; School Tours
                  </Link>
                  <Link to="/group-accommodation" className="u-navdrop__link" onClick={closeAll}>
                    Group Accommodation
                  </Link>
                </div>
              </div>
            )}
          </div>

          <Link to="/webcam">Webcams</Link>
          <Link to="/book-tour" className="u-bookbtn">
            Book a Tour
          </Link>
          {buyOpal(false)}
          {bagButton()}
        </nav>

        {/* mobile controls (≤1240px) */}
        <div className="u-mobbtn">
          <Link to="/book-tour" className="u-bookbtn--sm" onClick={closeAll}>
            Book
          </Link>
          {buyOpal(true)}
          {bagButton("u-bagbtn--sm")}
          <button
            type="button"
            className="u-hamburger"
            aria-label="Menu"
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen((o) => !o)}
          >
            ☰
          </button>
        </div>
      </div>

      {/* mobile slide-down panel */}
      {mobileOpen && (
        <nav className="u-mobpanel">
          <Link to="/about-opal" onClick={closeAll}>
            About Opal
          </Link>
          <Link to="/coober-pedy" onClick={closeAll}>
            Coober Pedy
          </Link>
          <Link to="/free-museum" onClick={closeAll}>
            Free Museum
          </Link>
          <Link to="/underground-tours" onClick={closeAll}>
            Underground Tours
          </Link>
          <Link to="/group-tours" onClick={closeAll}>
            Group &amp; School Tours
          </Link>
          <Link to="/group-accommodation" onClick={closeAll}>
            Group Accommodation
          </Link>
          <Link to="/opal-store" onClick={closeAll}>
            The Opal Store
          </Link>
          <Link to="/online-store" onClick={closeAll}>
            Shop Online
          </Link>
          <Link to="/webcam" onClick={closeAll}>
            Webcams
          </Link>
        </nav>
      )}
    </header>
  );
}
